/**
 * IndividualCitizenshipSection — the consolidated Citizenship block.
 *
 * Person-Modal consolidation (Bill 2026-06-11, LOCKED mockup v6 —
 * docs/operations/person-modal-inventory/06-INDIVIDUAL-SURFACE-CLEANUP-MOCKUP.html).
 * This block now sits at the TOP of the consolidated Individual body and
 * replaces BOTH prior citizenship surfaces:
 *   - the Stage-2 cohort screen (CitizenshipCohortGate.tsx — retired), and
 *   - the Origin-tab picker (CitizenshipPicker.tsx big artwork tiles — retired).
 *
 * Renders a compact 3-option segmented control with progressive disclosure:
 *   - US Citizen          → done after select (1 step)
 *   - Resident Alien      → + Country of citizenship (2 steps)
 *   - Non-Resident Alien  → Country + Purpose-of-stay cards (2 steps)
 *
 * Labels per the locked mockup: "US Citizen" / "Resident Alien" /
 * "Non-Resident Alien". Stored values are UNCHANGED (`RaoCitizenshipStatus`:
 * 'US Citizen' | 'Permanent Resident' | 'Non-Resident Alien') so the cohort
 * key ('permanent-resident' etc. via deriveCohortKey), cart persistence and
 * CustAdd/CustMod all read identically.
 *
 * NO VISA-CLASS INPUT (Bill 2026-06-11): the exact class (H-1B, F-1, …) has
 * no Core Director destination, so it is never typed. Azure DI reads it off
 * the captured visa stamp (normalizeVisaType → person.visaType) and the §5
 * eligibility rule fires from the EXTRACTED class at scan time
 * (isIneligibleVisaClass — individual-config.ts).
 *
 * Switch-citizenship guard: changing the cohort after captures exist
 * re-derives the tile set (the Documents grid below derives from the draft)
 * and WARNS about captured docs that no longer fit the new path — flag only,
 * captured data is never deleted.
 */

import { useEffect, useMemo } from 'react';
import type { RaoPerson, RaoCitizenshipStatus } from '../../../types';
import {
  NRA_STAY_PURPOSES,
  isIneligibleVisaClass,
  type StayPurpose,
} from './individual-config';
// Country dropdown data — cross-lane contract (Bill 2026-06-12): served by
// useCountryCatalog (BE lane owns the live /v3/config/countries
// implementation; this lane consumes the hook, which falls back to the
// legacy COUNTRIES list until the catalog loads).
import { useCountryCatalog } from './useCountryCatalog';
import { readAttachedDocs } from '../shared/attachedDocs';
import { cohortDocTiles, deriveCohortKey } from '../cohortDocTiles';
import { tileIdToCanonicalDocCode } from './captureRouting';
// Citizenship tile icons (Bill 2026-06-12) — the three canonical 12-citizenship
// catalog PNGs, already shipped INSIDE the app at
// apps/ROP-teams/public/icons/citizenship/ and resolved via the typed
// flat-library manifest (the app's established icon-asset pattern — same
// mechanism as every docs-grid / bucket tile icon; assets deploy with the
// bundle, no path reference leaves the app). Rendered SMALL — the segmented
// control stays compact (not the retired giant-artwork tiles).
import { citizenshipIconUrl } from '@/icons/flat-library';

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 360,
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const requiredErrStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: 'var(--pcb-red, #b10505)',
};

const readOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

function changedFieldStyle(base: React.CSSProperties, changed: boolean): React.CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

const bannerBase: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 6,
  fontSize: 13,
  lineHeight: 1.5,
};

const stepStyle: React.CSSProperties = {
  display: 'flex',
  gap: 11,
  alignItems: 'flex-start',
  border: '1px solid var(--ink-200)',
  borderRadius: 9,
  padding: '11px 13px',
  background: 'var(--surface-subtle, #f7f9fc)',
};

const stepDoneStyle: React.CSSProperties = {
  ...stepStyle,
  background: 'var(--ok-bg, #f0f8ee)',
  borderColor: 'var(--ok-line)',
};

function stepBadge(done: boolean, content: string): JSX.Element {
  return (
    <div
      aria-hidden
      style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: done ? 'var(--ok-fill)' : 'var(--brand-fill)',
        color: 'var(--on-brand)',
        fontSize: 11.5,
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 22px',
        marginTop: 1,
      }}
    >
      {content}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Segmented-control option metadata (compact — no artwork tiles)
// -----------------------------------------------------------------------------

interface CohortOption {
  /** Stored RaoCitizenshipStatus value — UNCHANGED code key. */
  readonly value: RaoCitizenshipStatus;
  /** Display label per the locked mockup v6. */
  readonly label: string;
  readonly description: string;
  readonly steps: string;
}

const COHORT_OPTIONS: ReadonlyArray<CohortOption> = [
  {
    value: 'US Citizen',
    label: 'US Citizen',
    description: 'Standard CIP — SSN required.',
    steps: '1 STEP',
  },
  {
    value: 'Permanent Resident',
    label: 'Resident Alien',
    // Bill 2026-06-12 — align the two ITIN wordings (this subtitle + the PR
    // banner below both read "ITIN may be used if SSN not yet assigned").
    description: 'Green Card (I-551) holder — W-9; ITIN may be used if SSN not yet assigned.',
    steps: '2 STEPS · + COUNTRY',
  },
  {
    value: 'Non-Resident Alien',
    label: 'Non-Resident Alien',
    description: 'Enhanced CIP — foreign ID + valid visa + W-8BEN.',
    steps: '2 STEPS · COUNTRY → PURPOSE',
  },
];

// -----------------------------------------------------------------------------
// Switch-citizenship guard — captured docs that no longer fit the new path
// -----------------------------------------------------------------------------

/** Friendly labels for the canonical Stage-9 DocCodes surfaced by the guard. */
const DOC_CODE_LABELS: Readonly<Record<string, string>> = {
  GOVT_ID: 'Photo ID',
  PASSPORT: 'Passport',
  GREEN_CARD: 'Green Card (I-551)',
  VISA: 'US Visa',
  I94: 'I-94',
  I20: 'I-20',
  DS2019: 'DS-2019',
  EAD: 'EAD (I-766)',
  SSN_CARD: 'SSN Card',
  ADDR_PROOF: 'Address Proof',
  BIRTH_CERT: 'Birth Certificate',
  ITIN_LTR: 'ITIN Letter',
  I797: 'I-797 approval notice',
  VISA_CLASS_PROOF: 'Visa-class proof',
  OTHER: 'Other Document',
};

function docCodeLabel(docCode: string): string {
  const second = docCode.endsWith('_2');
  const base = second ? docCode.slice(0, -2) : docCode;
  const label = DOC_CODE_LABELS[base] ?? base;
  return second ? `${label} (2nd)` : label;
}

/**
 * Photo-ID family — any of these canonical codes can land via the GENERIC
 * primary-id / secondary-id slots (the slot adopts the classified type), so
 * cohorts that carry a generic slot accept the whole family.
 */
const PHOTO_ID_FAMILY: ReadonlyArray<string> = [
  'GOVT_ID', 'PASSPORT', 'GREEN_CARD', 'SSN_CARD', 'BIRTH_CERT',
];

/**
 * Captured docs (person.extra.attachedDocs) whose canonical docCode does NOT
 * fit the current cohort's derived tile set. Flag-only — nothing is deleted;
 * the captures stay attached for audit and the FSR reviews them.
 */
function orphanedCapturesForCohort(person: RaoPerson): ReadonlyArray<{ docCode: string; filename: string }> {
  if (!person.citizenshipStatus) return [];
  const docs = readAttachedDocs(person);
  if (docs.length === 0) return [];

  const tiles = cohortDocTiles(deriveCohortKey(person));
  const allowed = new Set<string>();
  for (const t of tiles) {
    if (t.tileId === 'primary-id') {
      for (const c of PHOTO_ID_FAMILY) allowed.add(c);
      continue;
    }
    if (t.tileId === 'secondary-id') {
      for (const c of PHOTO_ID_FAMILY) allowed.add(`${c}_2`);
      continue;
    }
    allowed.add(tileIdToCanonicalDocCode(t.tileId));
  }
  // Address Proof renders beside the Address & Contact fields on EVERY cohort
  // (Bill 2026-06-11 relocation) and OTHER is the catch-all attach bucket —
  // both always fit.
  allowed.add('ADDR_PROOF');
  allowed.add('OTHER');

  return docs
    .filter((d) => !allowed.has(d.docCode))
    .map((d) => ({ docCode: d.docCode, filename: d.filename }));
}

// -----------------------------------------------------------------------------
// Extra-field helpers — stayPurpose lives on `person.extra`.
// -----------------------------------------------------------------------------

function readExtra(person: RaoPerson, key: string): string {
  const v = person.extra?.[key];
  return typeof v === 'string' ? v : '';
}

function patchExtra(person: RaoPerson, patch: Record<string, unknown>): Partial<RaoPerson> {
  const nextExtra = { ...(person.extra ?? {}), ...patch };
  return { extra: nextExtra };
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface IndividualCitizenshipSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  isChanged?: (field: string) => boolean;
  /** Notify parent when NRA + student triggers employer→school override. */
  onStudentVisaChange?: (isStudentVisa: boolean) => void;
  readOnly?: boolean;
  /**
   * Add-new mode only — cohort selection also derives customerType + tinCode
   * (mirrors the retired CitizenshipCohortGate pick: NRA → NonResidentAlien +
   * 'Forn', otherwise Individual + 'S'). Existing-CIF edit keeps the legacy
   * behavior (citizenship edits never silently flip the stored customerType).
   */
  syncCustomerType?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function IndividualCitizenshipSection({
  person,
  onChange,
  isChanged,
  onStudentVisaChange,
  readOnly = false,
  syncCustomerType = false,
}: IndividualCitizenshipSectionProps): JSX.Element {
  const ic = isChanged ?? ((_: string) => false);
  // Cross-lane contract (Bill 2026-06-12) — country dropdown reads the
  // catalog hook (BE lane implementation at merge; legacy-list fallback here).
  const { countries } = useCountryCatalog();
  // BACKLOG-0512 — read `citizenshipStatus` raw (no display fallback): add-new
  // opens UNSET (D2) and the hydration boundary normalizes existing CIFs.
  const citizenship = person.citizenshipStatus ?? '';
  const stayPurpose = readExtra(person, 'stayPurpose');
  // BACKLOG-0511 — canonical top-level field. Now populated ONLY by the visa
  // capture's extraction (normalizeVisaType) or CustInq hydration — never typed.
  const visaType = person.visaType ?? '';
  const country = person.countryOfCitizenship ?? '';

  const isPR = citizenship === 'Permanent Resident';
  const isNRA = citizenship === 'Non-Resident Alien';
  const isNonUs = isPR || isNRA;
  // Bill ruling 2026-06-12 (procedure-cited) — Visitor/Tourist purpose is NOT
  // eligible. Source: "Legacy Procedure - Non-Citizen ID Requirements.txt" —
  // "Non-Acceptable Visas: B (Visitor visa / tourism), VWP". Selecting the
  // card shows the plain-language explanation, derives NO document tiles, and
  // does NOT satisfy the citizenship gate (IndividualReviewBody.cohortReady).
  const visitorPurposeCard = NRA_STAY_PURPOSES.find((p) => p.ineligible);
  const visitorSelected =
    isNRA && !!visitorPurposeCard && stayPurpose === visitorPurposeCard.key;
  // §5 eligibility — fires from the EXTRACTED class at scan time (the visa
  // capture patches person.visaType live, so this banner appears on scan).
  const isIneligibleVisa = isNRA && isIneligibleVisaClass(visaType);
  const isStudentVisa = isNRA && stayPurpose === 'Student';

  // Notify parent (Employment section) whenever student-visa state changes.
  useEffect(() => {
    onStudentVisaChange?.(isStudentVisa);
  }, [isStudentVisa, onStudentVisaChange]);

  // Switch-citizenship guard — re-derives on every cohort change / capture.
  const orphanedCaptures = useMemo(() => orphanedCapturesForCohort(person), [person]);

  function onCohortChange(next: RaoCitizenshipStatus): void {
    if (readOnly || next === citizenship) return;
    const patch: Partial<RaoPerson> = { citizenshipStatus: next };
    if (next === 'US Citizen') {
      patch.countryOfCitizenship = 'US';
    } else if (citizenship === 'US Citizen' || citizenship === '') {
      // Coming from US/unset — clear the implicit 'US'. Switching between the
      // two non-US cohorts preserves an already-entered country.
      patch.countryOfCitizenship = '';
    }
    // The stated visa class derives from the captured stamp; a cohort change
    // invalidates the stated value (the capture itself stays attached — see
    // the orphaned-captures guard). stayPurpose only applies to NRA.
    patch.visaType = '';
    if (syncCustomerType) {
      // Mirrors the retired CitizenshipCohortGate.buildPick + PdwShell
      // blankPerson seeding for the add-new draft.
      patch.customerType = next === 'Non-Resident Alien' ? 'NonResidentAlien' : 'Individual';
      patch.tinCode = next === 'Non-Resident Alien' ? 'Forn' : 'S';
    }
    onChange({
      ...patch,
      ...(next !== 'Non-Resident Alien' ? patchExtra(person, { stayPurpose: '' }) : {}),
    });
  }

  function onPurposeChange(next: StayPurpose): void {
    if (readOnly) return;
    onChange(patchExtra(person, { stayPurpose: next }));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Compact 3-option segmented control */}
      <div role="radiogroup" aria-label="Citizenship status" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {COHORT_OPTIONS.map((opt) => {
          const selected = citizenship === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected ? 'true' : 'false'}
              disabled={readOnly}
              onClick={() => onCohortChange(opt.value)}
              style={{
                flex: '1 1 200px',
                minWidth: 200,
                textAlign: 'left',
                // Bill 2026-06-12: tiles stretch to equal height and a button
                // centers its content vertically, so the icon row drifted with
                // description length. Pin content to the tile's top edge so the
                // icon column aligns identically across all three tiles.
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'stretch',
                border: selected
                  ? '1.5px solid var(--pcb-green, #386732)'
                  : '1.5px solid var(--ink-200)',
                boxShadow: selected ? 'inset 0 0 0 1px var(--pcb-green, #386732)' : 'none',
                borderRadius: 9,
                padding: '10px 13px',
                background: selected ? 'var(--ok-bg, #f0f8ee)' : 'var(--surface-card, #fff)',
                cursor: readOnly ? 'not-allowed' : 'pointer',
                opacity: readOnly && !selected ? 0.65 : 1,
                transition: 'border-color 0.12s ease',
              }}
            >
              {/* Compact icon row (Bill 2026-06-12) — small catalog PNG
                  (flat-library CITIZENSHIP_ICONS, keyed by the stored
                  RaoCitizenshipStatus value) beside the label; NOT the
                  retired giant-artwork tiles. */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                <img
                  src={citizenshipIconUrl(opt.value)}
                  alt=""
                  aria-hidden
                  width={40}
                  height={40}
                  style={{ flex: '0 0 40px', marginTop: 1, borderRadius: 8, objectFit: 'contain' }}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-900)', marginBottom: 2 }}>
                    {selected && <span style={{ color: 'var(--pcb-green, #386732)' }}>✓ </span>}
                    {opt.label}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>{opt.description}</div>
                  <span
                    style={{
                      display: 'inline-block',
                      marginTop: 6,
                      fontSize: 9.5,
                      fontWeight: 800,
                      letterSpacing: '0.05em',
                      background: selected ? 'var(--ok-bg)' : 'var(--ink-100, #eef1f4)',
                      color: selected ? 'var(--pcb-green, #386732)' : 'var(--ink-600)',
                      borderRadius: 999,
                      padding: '2px 8px',
                    }}
                  >
                    {opt.steps}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {ic('citizenshipStatus') && (
        <div style={{ fontSize: 10, color: 'var(--pcb-blue)', marginTop: -6 }}>
          Changed from prior selection.
        </div>
      )}

      {/* Switch-citizenship guard — flag only, never deletes captured data */}
      {orphanedCaptures.length > 0 && (
        <div
          role="alert"
          style={{
            ...bannerBase,
            background: 'var(--pcb-amber-bg, #fef5e0)',
            border: '1px solid var(--pcb-amber-text, #e0a93a)',
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--pcb-amber-text, #8a6500)', marginBottom: 4 }}>
            Captured documents no longer fit this citizenship path
          </div>
          <div style={{ color: 'var(--ink-700)', fontSize: 12 }}>
            {orphanedCaptures.map((d) => `${docCodeLabel(d.docCode)} (${d.filename})`).join(' · ')}
            {' '}— the document set below re-derived for{' '}
            <strong>{COHORT_OPTIONS.find((o) => o.value === citizenship)?.label ?? citizenship}</strong>.
            Nothing was deleted; the captures stay attached for audit. Review whether they
            still apply or re-capture the documents this path requires.
          </div>
        </div>
      )}

      {/* Progressive disclosure — per-cohort steps. The unset-state footer
          line ("Select the citizenship status… No extra fields, no extra
          steps.") is REMOVED (Bill 2026-06-12: not factual, serves no
          purpose) — the section title carries the message and the rest of
          the modal stays hidden until a tile is picked (IndividualReviewBody
          hide-until-selected gating). */}
      {citizenship === 'US Citizen' && (
        <div style={stepDoneStyle}>
          {stepBadge(true, '✓')}
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-900)', marginBottom: 2 }}>
              Complete — no further citizenship steps
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>
              Standard CIP. SSN is captured on the Profile / SSN-TIN section (W-9 path).
              The documents to capture are ready below.
            </div>
          </div>
        </div>
      )}

      {isNonUs && (
        <div style={country ? stepDoneStyle : stepStyle}>
          {stepBadge(!!country, country ? '✓' : '1')}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-900)', marginBottom: 2 }}>
              Country of citizenship <span style={{ color: 'var(--pcb-red, #b10505)' }}>*</span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginBottom: 7 }}>
              {isPR
                ? 'Required for Resident Alien — feeds CIF country fields + the W-9 / ITIN ceremony.'
                : 'Required — feeds CIF country fields + W-8BEN.'}
            </div>
            <select
              id="rao-ind-country"
              value={country}
              onChange={(e) => onChange({ countryOfCitizenship: e.currentTarget.value })}
              disabled={readOnly}
              style={changedFieldStyle(
                readOnly ? readOnlyStyle : country ? inputStyle : requiredErrStyle,
                ic('countryOfCitizenship'),
              )}
              title="Country of Citizenship"
            >
              <option value="">-- Select country --</option>
              {/* Cross-lane contract (Bill 2026-06-12): options come from
                  useCountryCatalog (live CountryCatalog at merge; legacy-list
                  fallback until then). Stored value stays ISO alpha-2. */}
              {countries.map((c) => (
                <option key={c.iso2} value={c.iso2}>{c.name} ({c.iso2})</option>
              ))}
            </select>
            {!country && (
              <div style={{ fontSize: 10, color: 'var(--pcb-red, #b10505)', marginTop: 2 }}>Required</div>
            )}
          </div>
        </div>
      )}

      {/* NRA step 2 — purpose-of-stay cards (moved from CitizenshipCohortGate).
          Visitor/Tourist stays VISIBLE but renders INELIGIBLE (Bill ruling
          2026-06-12 — "Legacy Procedure - Non-Citizen ID Requirements.txt":
          "Non-Acceptable Visas: B (Visitor visa / tourism), VWP"): selecting
          it surfaces the explanation below, derives NO tiles, and never
          completes this step. */}
      {isNRA && (
        <div style={stayPurpose && !visitorSelected ? stepDoneStyle : stepStyle}>
          {stepBadge(!!stayPurpose && !visitorSelected, stayPurpose && !visitorSelected ? '✓' : '2')}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-900)', marginBottom: 2 }}>
              Purpose of stay <span style={{ color: 'var(--pcb-red, #b10505)' }}>*</span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginBottom: 7 }}>
              Drives the lawful-presence document set below.
            </div>
            <div
              role="radiogroup"
              aria-label="Purpose of stay"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
                gap: 8,
              }}
            >
              {NRA_STAY_PURPOSES.map((p) => {
                const selected = stayPurpose === p.key;
                const ineligible = !!p.ineligible;
                const selectedBorder = ineligible
                  ? '1.5px solid var(--pcb-red, #b10505)'
                  : '1.5px solid var(--pcb-green, #386732)';
                return (
                  <button
                    key={p.key}
                    type="button"
                    role="radio"
                    aria-checked={selected ? 'true' : 'false'}
                    disabled={readOnly}
                    onClick={() => onPurposeChange(p.key)}
                    style={{
                      textAlign: 'left',
                      border: selected ? selectedBorder : '1.5px solid var(--ink-200)',
                      boxShadow: selected
                        ? `inset 0 0 0 1px ${ineligible ? 'var(--pcb-red, #b10505)' : 'var(--pcb-green, #386732)'}`
                        : 'none',
                      borderRadius: 8,
                      background: selected
                        ? (ineligible ? 'var(--err-bg)' : 'var(--ok-bg, #f0f8ee)')
                        : 'var(--surface-card)',
                      padding: '8px 11px',
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                      opacity: ineligible && !selected ? 0.8 : 1,
                    }}
                  >
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-900)', marginBottom: 2 }}>
                      {selected && !ineligible && (
                        <span style={{ color: 'var(--pcb-green, #386732)' }}>✓ </span>
                      )}
                      {p.icon} {p.label}
                      {ineligible && (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 8.5,
                            fontWeight: 800,
                            letterSpacing: '0.05em',
                            background: 'var(--err-bg)',
                            color: 'var(--pcb-red, #b10505)',
                            borderRadius: 999,
                            padding: '1px 6px',
                            verticalAlign: 'middle',
                          }}
                        >
                          NOT ELIGIBLE
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--ink-600)' }}>{p.description}</div>
                  </button>
                );
              })}
            </div>
            {/* Ineligible-purpose explanation (procedure-cited — see
                visitorPurposeCard comment above). */}
            {visitorSelected && visitorPurposeCard?.ineligibleMessage && (
              <div
                role="alert"
                style={{
                  ...bannerBase,
                  marginTop: 8,
                  background: 'var(--err-bg)',
                  border: '1px solid var(--pcb-red, #b10505)',
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--pcb-red, #b10505)', marginBottom: 4 }}>
                  Visitor / Tourist
                </div>
                <div style={{ color: 'var(--ink-700)', fontSize: 12 }}>
                  {visitorPurposeCard.ineligibleMessage}
                </div>
              </div>
            )}
            {!stayPurpose && (
              <div style={{ fontSize: 10, color: 'var(--pcb-red, #b10505)', marginTop: 4 }}>Required</div>
            )}
          </div>
        </div>
      )}

      {/* NRA — visa class is read from the captured stamp, never typed.
          Reworded per Bill's NRA smoke test (2026-06-12): the old "No
          visa-class input — read from the document" copy read like the user
          had missed something. Calm informational row, not a warning. */}
      {isNRA && (
        <div style={stepDoneStyle}>
          {stepBadge(true, '✓')}
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-900)', marginBottom: 2 }}>
              Visa class is read from the document
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>
              Read from the captured visa to ensure accuracy — eligibility rules fire
              from the visa class upon extraction by the document analyzer.
              {visaType && (
                <>
                  {' '}Extracted class: <strong>{visaType}</strong>
                  {person.visaExpDt ? <> · expires {person.visaExpDt}</> : null}.
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Permanent (Resident Alien) info banner */}
      {isPR && (
        <div
          style={{
            ...bannerBase,
            background: 'var(--info-bg)',
            border: '1px solid var(--pcb-blue)',
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--pcb-blue)', marginBottom: 4 }}>
            Resident Alien — Green Card Holder
          </div>
          <div style={{ color: 'var(--ink-700)', fontSize: 12 }}>
            Collect copy of Permanent Resident Card (I-551 / Green Card).
            ITIN may be used if SSN not yet assigned. W-9 required (not W-8BEN).
          </div>
        </div>
      )}

      {/* Ineligible visa (RED) — fires from the EXTRACTED class (§5 catalog) */}
      {isIneligibleVisa && (
        <div
          role="alert"
          style={{
            ...bannerBase,
            background: 'var(--err-bg)',
            border: '1px solid var(--pcb-red, #b10505)',
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--pcb-red, #b10505)', marginBottom: 4 }}>
            Ineligible Visa Type — {visaType} (read from the captured visa)
          </div>
          <div style={{ color: 'var(--ink-700)' }}>
            Visitors on <strong>B-1/B-2 tourist visas</strong> or the{' '}
            <strong>Visa Waiver Program (VWP/ESTA)</strong> are not eligible to open accounts at PCB.
            This customer cannot proceed without a valid long-term visa (F-1, H-1B, L-1, J-1, EB,
            IR, DV, etc.).
          </div>
        </div>
      )}

      {/* Enhanced CIP banner (BLUE) — eligible NRA. Bill 2026-06-12 fixes:
            - "foreign address documentation" REMOVED from the required list
              (contradicts the 6/12 ruling that removed the foreign-residence
              section; the relocated Address-Verification tile covers address
              proof).
            - The "30-day address verification tickler" sentence replaced with
              the procedure-accurate ladder. Source: "Legacy Procedure -
              Address Verification.txt" — Core Director ticklers 513 (10-day
              reminder) / 514 (20-day + phone call) / 515 (30-day; unmet =
              account closed, cashier's check issued).
          Suppressed while the ineligible Visitor/Tourist purpose is selected
          (no Enhanced-CIP collect list for a path that cannot open). */}
      {isNRA && !isIneligibleVisa && !visitorSelected && (
        <div
          style={{
            ...bannerBase,
            background: 'var(--info-bg)',
            border: '1px solid var(--pcb-blue)',
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--pcb-blue)', marginBottom: 4 }}>
            Non-Resident Alien — Enhanced CIP Required
          </div>
          <div style={{ color: 'var(--ink-700)', fontSize: 12 }}>
            Required documents: passport + valid visa copy, W-8BEN form.
            If proof of address is still needed, follow-up runs on the bank&rsquo;s
            10/20/30-day reminder schedule — and if it isn&rsquo;t provided by day 30,
            the account is closed per procedure.
          </div>
        </div>
      )}
    </div>
  );
}
